import { useLayoutEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'uikit/Tooltip/Tooltip';
import { ClipBoard } from 'components/common/ClipBoard/ClipBoard';
import './CryptoAddress.scss';

const ELLIPSIS = '…';
let measureCanvas = null;

const measureTextWidth = (text, font) => {
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
  }
  const context = measureCanvas.getContext('2d');
  context.font = font;
  return context.measureText(text).width;
};

// Binary-search-ish shrink from the middle so the widest prefix+suffix that still fits is kept,
// instead of a fixed character count that overflows on some networks (BTC) and wastes space on others (TRX).
export const shortenAddressToFit = (address, availableWidth, font) => {
  if (!address) return '';
  if (!availableWidth || measureTextWidth(address, font) <= availableWidth) return address;

  let prefixLength = Math.ceil(address.length / 2);
  let suffixLength = address.length - prefixLength;

  while (prefixLength > 1 && suffixLength > 1) {
    const candidate = `${address.slice(0, prefixLength)}${ELLIPSIS}${address.slice(address.length - suffixLength)}`;
    if (measureTextWidth(candidate, font) <= availableWidth) {
      return candidate;
    }
    if (prefixLength > suffixLength) {
      prefixLength -= 1;
    } else {
      suffixLength -= 1;
    }
  }

  return `${address.slice(0, 1)}${ELLIPSIS}${address.slice(-1)}`;
};

const CryptoAddress = ({ address, className, withCopy = true }) => {
  const containerRef = useRef(null);
  const [displayValue, setDisplayValue] = useState(address);

  const recalculate = useCallback(() => {
    const node = containerRef.current;
    if (!node || !address) return;

    const font = window.getComputedStyle(node).font;
    // offsetWidth rounds to an integer and can report more space than there is, so floor the real width.
    setDisplayValue(shortenAddressToFit(address, Math.floor(node.getBoundingClientRect().width), font));
  }, [address]);

  useLayoutEffect(() => {
    recalculate();

    const node = containerRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(recalculate);
    observer.observe(node);
    return () => observer.disconnect();
  }, [recalculate]);

  if (!address) return null;

  const isTruncated = displayValue !== address;

  return (
    <span className={`crypto-address ${className || ''}`}>
      <Tooltip content={address} disabled={!isTruncated} direction={'top'} className={'crypto-address-tooltip'}>
        <span ref={containerRef} className={'crypto-address-value'}>
          {displayValue}
        </span>
      </Tooltip>
      {withCopy && <ClipBoard copyText={address} />}
    </span>
  );
};

CryptoAddress.propTypes = {
  address: PropTypes.string,
  className: PropTypes.string,
  withCopy: PropTypes.bool
};

export default CryptoAddress;
